import { XStack, YStack } from '@lyra/core/components'
import BodyText from '@lyra/core/components/BodyText'
import Button from '@lyra/core/components/Button'
import Checkbox from '@lyra/core/components/Checkbox'
import DraggableContainer, { DropResult, reorder } from '@lyra/core/components/DraggableContainer'
import { Plus } from '@lyra/core/components/Icon'
import LabelText from '@lyra/core/components/LabelText'
import Modal from '@lyra/core/components/Modal'
import Section from '@lyra/core/components/Section'
import useUserSettings from '@lyra/web/hooks/useUserSettings'
import {
  getOptionsChainPreset,
  isOptionsChainPreset,
  OPTIONS_CUSTOM_COLUMN_MAX,
  OptionsChainColumn,
  OptionsChainPreset,
} from '@lyra/web/utils/userSettings'
import { useCallback, useMemo } from 'react'

import { getCustomColumnLabel } from '../TradeOptionTable'
import OptionsColumnDraggableItem from './OptionsColumnDraggableItem'

const BUTTON_WIDTH = 170
const TRADER_SETTINGS_MODAL_WIDTH = 600

type Props = {
  onClose: () => void
}

const TradeOptionSettingsModal = ({ onClose }: Props) => {
  const { userSettings, updateSettings } = useUserSettings()

  const selectedPreset = useMemo(
    () => isOptionsChainPreset(userSettings.optionsChain),
    [userSettings.optionsChain]
  )

  const visibleColumns = userSettings.optionsChain.columns

  const availableColumns = useMemo(() => {
    return Object.values(OptionsChainColumn).filter((col) => !visibleColumns.includes(col))
  }, [visibleColumns])

  const isAvailableDisabled = userSettings.optionsChain.columns.length >= OPTIONS_CUSTOM_COLUMN_MAX

  const onItemsReorder = useCallback(
    (result: DropResult) => {
      if (!result.destination) {
        return
      }
      const reorderedItems = reorder(visibleColumns, result.source.index, result.destination.index)

      updateSettings('optionsChain', {
        ...userSettings.optionsChain,
        columns: reorderedItems as OptionsChainColumn[],
      })
    },
    [updateSettings, userSettings.optionsChain, visibleColumns]
  )

  return (
    <Modal
      onClose={onClose}
      title="Customize Chain"
      subtitle="Customize your options chain columns."
      desktopWidth={TRADER_SETTINGS_MODAL_WIDTH}
      isLarge
    >
      <Section.YStack>
        <LabelText>Presets</LabelText>
        <XStack flexGrow={1} gap="$2">
          {Object.values(OptionsChainPreset).map((preset) => (
            <Button
              key={preset}
              label={preset}
              width={BUTTON_WIDTH}
              isSelected={preset === selectedPreset}
              onPress={() => {
                if (preset !== selectedPreset) {
                  updateSettings('optionsChain', getOptionsChainPreset(preset))
                }
              }}
            />
          ))}
        </XStack>
      </Section.YStack>
      <Section.Separator />
      <Section.XStack>
        <YStack gap="$2" width={BUTTON_WIDTH}>
          <LabelText>Visible Columns</LabelText>
          <DraggableContainer
            direction="vertical"
            onDragEnd={onItemsReorder}
            items={visibleColumns.map((customCol: OptionsChainColumn, idx: number) => {
              return {
                id: customCol,
                content: (
                  <OptionsColumnDraggableItem
                    key={idx}
                    label={getCustomColumnLabel(customCol)}
                    width={BUTTON_WIDTH}
                    marginBottom="$2"
                    isDisabled={visibleColumns.length <= 2}
                    onPress={() => {
                      updateSettings('optionsChain', {
                        ...userSettings.optionsChain,
                        columns: userSettings.optionsChain.columns.filter(
                          (col: OptionsChainColumn) => col !== customCol
                        ),
                      })
                    }}
                  />
                ),
              }
            })}
          />
        </YStack>
        <YStack gap="$2" flexGrow={1} flexShrink={1}>
          <LabelText>Available Columns</LabelText>
          <YStack
            width="100%"
            style={{
              display: 'grid',
              gap: '12px',
              gridTemplateColumns: 'repeat(2, 1fr)',
              overflow: 'auto',
            }}
          >
            {availableColumns.map((colId: OptionsChainColumn) => {
              return (
                <Button
                  width={BUTTON_WIDTH}
                  key={colId}
                  label={getCustomColumnLabel(colId)}
                  icon={<Plus />}
                  isDisabled={isAvailableDisabled}
                  onPress={() => {
                    updateSettings('optionsChain', {
                      ...userSettings.optionsChain,
                      columns: [...userSettings.optionsChain.columns, colId],
                    })
                  }}
                />
              )
            })}
          </YStack>
        </YStack>
      </Section.XStack>
      <Section.YStack>
        <LabelText>Settings</LabelText>
        <XStack space="$3">
          <XStack
            width={BUTTON_WIDTH}
            alignItems="center"
            space="$1"
            cursor="pointer"
            onPress={() =>
              updateSettings('optionsChain', {
                ...userSettings.optionsChain,
                showBidAskSize: !userSettings.optionsChain.showBidAskSize,
              })
            }
          >
            <Checkbox isChecked={userSettings.optionsChain.showBidAskSize} />
            <BodyText>Show Bid / Ask Size</BodyText>
          </XStack>
          <XStack
            width={BUTTON_WIDTH}
            alignItems="center"
            space="$1"
            cursor="pointer"
            onPress={() =>
              updateSettings('optionsChain', {
                ...userSettings.optionsChain,
                showBidAskIv: !userSettings.optionsChain.showBidAskIv,
              })
            }
          >
            <Checkbox isChecked={userSettings.optionsChain.showBidAskIv} />
            <BodyText>Show Bid / Ask IV</BodyText>
          </XStack>
        </XStack>
        <XStack space="$3">
          <XStack
            width={BUTTON_WIDTH}
            alignItems="center"
            space="$1"
            cursor="pointer"
            onPress={() =>
              updateSettings('optionsChain', {
                ...userSettings.optionsChain,
                defaultToCalls: !userSettings.optionsChain.defaultToCalls,
              })
            }
          >
            <Checkbox isChecked={userSettings.optionsChain.defaultToCalls} />
            <BodyText>Default To Calls</BodyText>
          </XStack>
          <XStack
            width={BUTTON_WIDTH}
            alignItems="center"
            space="$1"
            cursor="pointer"
            onPress={() =>
              updateSettings('optionsChain', {
                ...userSettings.optionsChain,
                showMarkPrice: !userSettings.optionsChain.showMarkPrice,
              })
            }
          >
            <Checkbox isChecked={userSettings.optionsChain.showMarkPrice} />
            <BodyText>Show Mark Price</BodyText>
          </XStack>
        </XStack>
      </Section.YStack>
    </Modal>
  )
}

export default TradeOptionSettingsModal
